import type { AutoDomainAnalysisRow, Vantage } from './autoDomainTypes'
import type { BrowserRow } from '../ProcessedEntry/DomainEntry/processedTypes'
import { findDescription } from '../ProcessedEntry/DomainEntry/processedTypes'

// One browser capture, whichever table it came from -- the row shape
// ScreenshotCaptures renders. Two sources today:
//   - auto_domain_analysis (a candidate's browser pass): captureFromAnalysisRow
//   - entry_browser_data (an entry's capture):            captureFromBrowserRow
// Both store the same R2 file (scenarios.{desktop,mobile}.screenshots[]),
// readable through POST /api/screenshots/bykey with the row's r2Key, so the
// widget only needs the key plus whatever metadata each side can offer: a
// candidate row knows its lane and proxy, an entry row its LLM captions and
// what the page looked like (final URL, status, parked/blocked).

export type MobileEngine = 'iPhone' | 'old mobile'

export type ScreenshotCapture = {
  id: string
  r2Key: string | null
  createdAt: string
  desktopIsAlive: boolean | null
  mobileIsAlive: boolean | null
  /** The session says it captured something -- shown as "captured, not
   * stored" when r2Key is null, "no screenshots captured" otherwise. */
  hasScreenshots?: boolean | null
  /** Candidate passes only. */
  vantage?: Vantage | null
  proxyLabel?: string | null
  mobileEngine?: MobileEngine | null
  finalUrl?: string | null
  statusCode?: number | null
  parked?: boolean | null
  blocked?: boolean | null
  findings?: Array<string | { description?: string; type?: string }>
  /** Entry captures only: the LLM's one-line description of each screenshot. */
  descriptions?: { mobile: string | null; desktop: string | null }
}

// The mobile scenario the browser session actually ran: it stamps
// browser_type on the scenario -- 'webkit_iphone17' for the real iPhone,
// anything else (the session's own fetcher) for the iPhone User-Agent on
// desktop Chromium, which is also what an iPhone pass silently falls back to
// without WebKit.
export const mobileEngineOfBrowserType = (bt: unknown): MobileEngine | null =>
  bt ? (bt === 'webkit_iphone17' ? 'iPhone' : 'old mobile') : null

export function captureFromAnalysisRow(row: AutoDomainAnalysisRow): ScreenshotCapture {
  const r = row.raw_result ?? {}
  return {
    id: row.id,
    r2Key: row.r2_key,
    createdAt: row.created_at,
    desktopIsAlive: r.desktop_is_alive ?? null,
    mobileIsAlive: r.mobile_is_alive ?? null,
    hasScreenshots: r.has_screenshots ?? null,
    vantage: row.vantage,
    proxyLabel: row.proxy_label,
    mobileEngine: mobileEngineOfBrowserType(r.postgres_data?.scenarios?.mobile?.browser_type),
    findings: Array.isArray(r.security_findings) ? r.security_findings : [],
  }
}

export function captureFromBrowserRow(row: BrowserRow): ScreenshotCapture {
  const meta = row.rawBrowserMetadata ?? {}
  const d = meta.scenarios?.desktop ?? {}
  const m = meta.scenarios?.mobile ?? {}
  const captured = d.screenshot_captured || m.screenshot_captured
  return {
    id: row.id,
    r2Key: row.r2Key,
    createdAt: row.createdAt,
    desktopIsAlive: row.desktopIsAlive,
    mobileIsAlive: row.mobileIsAlive,
    hasScreenshots: typeof captured === 'boolean' ? captured : null,
    mobileEngine: mobileEngineOfBrowserType(m.browser_type),
    finalUrl: d.final_url ?? m.final_url ?? null,
    statusCode: d.status_code ?? m.status_code ?? null,
    parked: d.is_parked ?? m.is_parked ?? null,
    blocked: d.is_blocked ?? m.is_blocked ?? null,
    findings: Array.isArray(meta.security_findings) ? meta.security_findings : [],
    descriptions: {
      mobile: findDescription(row.descriptions, 'screenshot_mobile_description'),
      desktop: findDescription(row.descriptions, 'screenshot_desktop_description'),
    },
  }
}

/** Newest first, whatever order the caller holds (ISO timestamps compare as strings). */
export const sortCapturesNewestFirst = (captures: ScreenshotCapture[]): ScreenshotCapture[] =>
  [...captures].sort((a, b) => (b.createdAt > a.createdAt ? 1 : b.createdAt < a.createdAt ? -1 : 0))
